
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, MapPin, Clock } from 'lucide-react';
import { TerraceSubmission } from '@/types/terrace';

interface SubmissionCardProps {
  submission: TerraceSubmission;
}

const SubmissionCard = ({ submission }: SubmissionCardProps) => {
  const getStatusBadge = (status: 'pending' | 'approved' | 'rejected') => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500 text-white">Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500 text-white">Pending Review</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 text-white">Rejected</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl">{submission.name}</CardTitle>
          {getStatusBadge(submission.status)}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {submission.address}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarDays className="h-4 w-4" />
              Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
            </div>
            {submission.openingHours && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {submission.openingHours}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium">Type:</span> {submission.type}
            </div>
            <div className="flex gap-2 flex-wrap">
              {submission.hasFood && (
                <Badge variant="outline">Has Food</Badge>
              )}
              {submission.servesAlcohol && (
                <Badge variant="outline">Serves Alcohol</Badge>
              )}
              {submission.petFriendly && (
                <Badge variant="outline">Pet Friendly</Badge>
              )}
            </div>
          </div>
        </div>
        
        {submission.terraceDescription && (
          <p className="text-sm text-muted-foreground">
            {submission.terraceDescription}
          </p>
        )}

        {submission.status === 'approved' && (
          <div className="pt-2 border-t">
            <p className="text-sm text-green-600 font-medium">
              🎉 Your submission has been approved and is now live on our platform!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SubmissionCard;
